/**
 * stockLedger.ts — Pure computation of a single product's stock ledger: every
 * movement (sale, purchase, return, adjustment, transfer…) with a running
 * balance, for shrinkage investigation and audit.
 *
 * The signed stock delta of each movement is NOT recomputed here — it is passed
 * in, already derived by the canonical `productStockDelta` (src/sync/liveStock),
 * which mirrors the backend derivation that produces `product.currentStock`.
 * Reusing it is the whole point: the ledger's balances reconcile to the stock
 * figure the rest of the app shows, instead of inventing a second sign
 * convention (the movement table's raw `quantity` sign is inconsistent across
 * writers, so it cannot be trusted directly).
 *
 * Balances are anchored to `currentStock` (the live figure as of now) and worked
 * backwards: the opening balance at the range start is
 *   currentStock − Σ(delta of every movement on/after the range start),
 * so the closing balance reconciles even when the range ends in the past.
 *
 * Pure — see `stockLedgerDataSource.ts` for the WatermelonDB bridge.
 */

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface LedgerMovementInput {
  date: Date;
  /** Raw movement type (OUT, PURCHASE, ADJUSTMENT, …) for labelling. */
  type: string;
  /** Canonical signed stock delta from productStockDelta. */
  delta: number;
  referenceType: string | null;
  notes: string | null;
  userName: string | null;
}

export interface StockLedgerInput {
  /** All movements on/after `from` (used to anchor the opening balance). */
  movements: LedgerMovementInput[];
  from: Date;
  /** Exclusive. */
  to: Date;
  /** Live product stock as of now. */
  currentStock: number;
}

// ─── Output ──────────────────────────────────────────────────────────────────

export type Direction = 'IN' | 'OUT' | 'NEUTRAL';

export interface LedgerRow {
  date: Date;
  type: string;
  typeLabel: string;
  direction: Direction;
  delta: number;
  balanceAfter: number;
  referenceType: string | null;
  notes: string | null;
  userName: string | null;
}

export interface LedgerTypeSummary {
  type: string;
  label: string;
  count: number;
  delta: number;
}

export interface StockLedger {
  openingBalance: number;
  closingBalance: number;
  /** Live stock now; equals closingBalance when the range ends at/after now. */
  currentStock: number;
  totalIn: number;
  totalOut: number;
  net: number;
  rows: LedgerRow[];
  byType: LedgerTypeSummary[];
}

const TYPE_LABELS: Record<string, string> = {
  OUT: 'Sale',
  SALE: 'Sale',
  IN: 'Sales return',
  RETURN: 'Sales return',
  PURCHASE: 'Purchase',
  PURCHASE_RECEIVE: 'Purchase',
  PURCHASE_RETURN: 'Purchase return',
  ADJUSTMENT: 'Adjustment',
  OPENING: 'Opening',
  DAMAGE: 'Damage',
  TRANSFER_IN: 'Transfer in',
  TRANSFER_OUT: 'Transfer out',
  IMPORT: 'Import',
};

function labelOf(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

function directionOf(delta: number): Direction {
  if (delta > 0) return 'IN';
  if (delta < 0) return 'OUT';
  return 'NEUTRAL';
}

export function buildStockLedger(input: StockLedgerInput): StockLedger {
  const { movements, from, to, currentStock } = input;

  // Opening = stock before `from` = currentStock minus everything on/after `from`.
  const onOrAfterFrom = movements.filter((m) => m.date.getTime() >= from.getTime());
  const deltaOnOrAfterFrom = onOrAfterFrom.reduce((s, m) => s + m.delta, 0);
  const openingBalance = round2(currentStock - deltaOnOrAfterFrom);

  // In-range movements, ascending, with a running balance from the opening.
  const inRange = onOrAfterFrom
    .filter((m) => m.date.getTime() < to.getTime())
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  let running = openingBalance;
  let totalIn = 0;
  let totalOut = 0;
  const typeMap = new Map<string, { count: number; delta: number }>();

  const rows: LedgerRow[] = inRange.map((m) => {
    running = round2(running + m.delta);
    if (m.delta > 0) totalIn += m.delta;
    else if (m.delta < 0) totalOut += -m.delta;
    const t = typeMap.get(m.type) ?? { count: 0, delta: 0 };
    t.count += 1;
    t.delta += m.delta;
    typeMap.set(m.type, t);
    return {
      date: m.date,
      type: m.type,
      typeLabel: labelOf(m.type),
      direction: directionOf(m.delta),
      delta: round2(m.delta),
      balanceAfter: running,
      referenceType: m.referenceType,
      notes: m.notes,
      userName: m.userName,
    };
  });

  const byType: LedgerTypeSummary[] = [...typeMap.entries()]
    .map(([type, v]) => ({ type, label: labelOf(type), count: v.count, delta: round2(v.delta) }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

  return {
    openingBalance,
    closingBalance: round2(running),
    currentStock: round2(currentStock),
    totalIn: round2(totalIn),
    totalOut: round2(totalOut),
    net: round2(totalIn - totalOut),
    rows,
    byType,
  };
}
